import { createReducer, on } from "@ngrx/store";

import { ProductFeatureActions } from ".";
import { ProductFeature } from "./product-feature.models";

export interface ProductFeatureState {
  productFeatures: ProductFeature[] | null;
  loadProductFeaturesError: string | null;
}

export const initialState: ProductFeatureState = {
  productFeatures: null,
  loadProductFeaturesError: null,
};

// Helper function to update a specific product feature
const updateProductFeature = (
  state: ProductFeatureState,
  id: string,
  updateFn: (feature: ProductFeature) => ProductFeature
): ProductFeatureState => {
  const currentProductFeatures = Array.isArray(state.productFeatures)
    ? state.productFeatures
    : [];

  return {
    ...state,
    productFeatures: currentProductFeatures.map(feature =>
      feature.id === id ? updateFn(feature) : feature
    ),
  };
};

export const productFeatureReducer = createReducer(
  initialState,

  // Get feature actions
  on(ProductFeatureActions.getProductFeatures, state => ({
    ...state,
    loadProductFeaturesError: null,
  })),

  on(
    ProductFeatureActions.getProductFeaturesSuccess,
    (state, { productFeatures }) => ({
      ...state,
      productFeatures,
    })
  ),

  on(ProductFeatureActions.getProductFeaturesFailure, (state, { error }) => ({
    ...state,
    loadProductFeaturesError: error,
  })),

  // Enable feature actions
  on(ProductFeatureActions.enableProductFeature, (state, { id }) =>
    updateProductFeature(state, id, feature => ({
      ...feature,
      isEnabled: true,
      isProcessing: true,
    }))
  ),

  on(ProductFeatureActions.enableProductFeatureSuccess, (state, { id }) =>
    updateProductFeature(state, id, feature => ({
      ...feature,
      isEnabled: true,
      isProcessing: false,
    }))
  ),

  on(ProductFeatureActions.enableProductFeatureFailure, (state, { id }) =>
    updateProductFeature(state, id, feature => ({
      ...feature,
      isEnabled: false,
      isProcessing: false,
    }))
  ),

  // Disable feature actions
  on(ProductFeatureActions.disableProductFeature, (state, { id }) =>
    updateProductFeature(state, id, feature => ({
      ...feature,
      isEnabled: false,
      isProcessing: true,
    }))
  ),

  on(ProductFeatureActions.disableProductFeatureSuccess, (state, { id }) =>
    updateProductFeature(state, id, feature => ({
      ...feature,
      isEnabled: false,
      isProcessing: false,
    }))
  ),

  on(ProductFeatureActions.disableProductFeatureFailure, (state, { id }) =>
    updateProductFeature(state, id, feature => ({
      ...feature,
      isEnabled: true,
      isProcessing: false,
    }))
  )
);
